import React, { useRef, useState } from 'react';

import { Button } from '@copilot/ui';
import Icon from '../common/Icon';
import * as documentService from '../../services/documentService';
import { reportError } from '../../services/errorTracking';
import {
  DOCUMENT_ACCEPT,
  DOCUMENT_EXTENSIONS,
  DOCUMENT_UNSUPPORTED_MESSAGE,
  extensionOf,
} from '../../utils/documentTypes';

function DocumentUpload({ onUploaded }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const inputRef = useRef(null);

  const handleFileChange = (event) => {
    setError('');
    setSuccessMessage('');
    setSelectedFile(event.target.files?.[0] || null);
  };

  const handleUpload = async () => {
    setError('');
    setSuccessMessage('');

    if (!selectedFile) {
      setError('Choose a file to upload.');
      return;
    }

    if (!DOCUMENT_EXTENSIONS.includes(extensionOf(selectedFile.name))) {
      setError(DOCUMENT_UNSUPPORTED_MESSAGE);
      return;
    }

    setIsUploading(true);

    try {
      const document = await documentService.uploadDocument(selectedFile);
      setSuccessMessage(`${document.file_name} uploaded.`);
      setSelectedFile(null);
      if (inputRef.current) inputRef.current.value = '';
      onUploaded?.(document);
    } catch (err) {
      reportError('document_upload', err);
      setError(err.message || 'Unable to upload document.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="document-upload">
      <label className="document-upload-dropzone" htmlFor="document-upload-input">
        <Icon name="upload" size={22} />
        <span>
          {selectedFile ? selectedFile.name : 'Choose a PDF, Word, PowerPoint, Excel, CSV or TXT file'}
        </span>
        <input
          id="document-upload-input"
          ref={inputRef}
          type="file"
          accept={DOCUMENT_ACCEPT}
          onChange={handleFileChange}
          disabled={isUploading}
        />
      </label>

      {/* type="button" - this component is also embedded inside the
          meeting-creation form (pages/StartMeetingPage.jsx); without an
          explicit type, a bare <button> defaults to type="submit" and
          this click would submit that outer form instead of just
          uploading the file. */}
      <Button
        type="button"
        onClick={handleUpload}
        disabled={isUploading || !selectedFile}
      >
        {isUploading ? 'Uploading...' : 'Upload Document'}
      </Button>

      {error && <div className="form-alert">{error}</div>}
      {successMessage && (
        <div className="document-upload-success">{successMessage}</div>
      )}
    </div>
  );
}

export default DocumentUpload;
